<template>
    <div class="bulk-section" v-if="selectedJobs.length > 1">
        <div>
            <span>{{ selectedJobs.length }}</span>
            job{{ selectedJobs.length > 1 ? 's' : '' }} selected.
        </div>
        <div>
            <md-button class="md-round md-just-icon md-primary" title="Recalculate jobs" @click="bulkRecalculate">
                <md-icon>currency_exchange</md-icon>
            </md-button>
            <md-button class="md-round md-just-icon md-success" title="Approve jobs" @click="bulkApprove">
                <md-icon>thumb_up</md-icon>
            </md-button>
            <md-button class="md-round md-just-icon md-danger" title="Unapprove jobs" @click="bulkUnapprove">
                <md-icon>thumb_down</md-icon>
            </md-button>
        </div>
    </div>
</template>

<script>
import { handleRequests, showErrorMessage } from '@/helpers';

export default {
    name: 'BulkActions',
    props: {
        selectedJobs: {
            type: Array,
            required: true
        },
        triggerBulkRecalculate: {
            type: Function,
            required: true
        },
        /**
         * Type of process charges: 'stop', 'trip', or 'shipment'
         * Determines which IDs to use for recalculation
         */
        type: {
            type: String,
            required: true,
            validator: (value) => ['stop', 'trip', 'shipment'].includes(value)
        }
    },
    methods: {
        getPayload() {
            const tripIds = [];
            const stopIds = [];
            const shipmentIds = [];

            this.selectedJobs.forEach((item) => {
                if (item.tripId) {
                    tripIds.push(item.tripId);
                }

                if (item.stopId) {
                    stopIds.push(item.stopId);
                }

                if (item.shipmentId) {
                    shipmentIds.push(item.shipmentId);
                }
            });
            return {
                tripIds,
                stopIds,
                shipmentIds
            };
        },
        bulkRecalculate() {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Recalculate',
                    body: `Are you sure you want to recalculate all job/s?`,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        // Filter items that have rate groups assigned
                        const idsToRecalculate = [];

                        this.selectedJobs.forEach((item) => {
                            if (item.rateGroupCostId || item.rateGroupChargeId) {
                                if (this.type === 'stop' && item.stopId) {
                                    idsToRecalculate.push(item.stopId);
                                } else if (this.type === 'trip' && item.tripId) {
                                    idsToRecalculate.push(item.tripId);
                                } else if (this.type === 'shipment' && item.shipmentId) {
                                    idsToRecalculate.push(item.shipmentId);
                                }
                            }
                        });

                        if (idsToRecalculate.length <= 0) {
                            return;
                        }

                        // Build the payload based on type
                        let toUpdate;
                        if (this.type === 'stop') {
                            toUpdate = { stopIds: idsToRecalculate };
                        } else if (this.type === 'trip') {
                            toUpdate = { tripIds: idsToRecalculate };
                        } else {
                            toUpdate = { shipmentIds: idsToRecalculate };
                        }

                        const payload = {
                            method: 'post',
                            data: toUpdate
                        };

                        this.triggerBulkRecalculate(idsToRecalculate);

                        const api = `/api/rates/recalculate-rates-Batch`;
                        try {
                            await handleRequests(api, payload);
                        } catch (e) {
                            const message = `Cannot recalculate, please contact support if issue persists`;
                            showErrorMessage(this, message, e);
                        }
                    }
                });
        },
        bulkApprove() {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Approve',
                    body: `Are you sure you want to approve all job/s?`,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response !== 'Confirm') {
                        return;
                    }

                    const data = this.getPayload();

                    if (data.stopIds.length <= 0 && data.shipmentIds.length <= 0 && data.tripIds.length <= 0) {
                        return;
                    }

                    const payload = {
                        method: 'post',
                        data
                    };

                    const api = `/api/process-charges/approve-jobs`;
                    try {
                        await handleRequests(api, payload);

                        this.$notifySuccess('Jobs approved.');
                    } catch (e) {
                        const message = `Cannot approve, please contact support if issue persists`;
                        showErrorMessage(this, message, e);
                    }
                });
        },
        bulkUnapprove() {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Unapprove',
                    body: `Are you sure you want to unapprove all job/s?`,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response !== 'Confirm') {
                        return;
                    }

                    const data = this.getPayload();

                    if (data.stopIds.length <= 0 && data.shipmentIds.length <= 0 && data.tripIds.length <= 0) {
                        return;
                    }

                    const payload = {
                        method: 'post',
                        data
                    };

                    const api = `/api/process-charges/unapprove-jobs`;
                    try {
                        await handleRequests(api, payload);

                        this.$notifySuccess(`Jobs unapproved.`);
                    } catch (e) {
                        const message = `Cannot unapprove, please contact support if issue persists`;
                        showErrorMessage(this, message, e);
                    }
                });
        }
    }
};
</script>
<style lang="scss" scoped>
.bulk-section {
    max-width: 500px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px;
    background-color: #fafafb;
    border: 1px solid #ddd;
    margin: 0 auto;
    margin-bottom: 10px;
    border-radius: 6px;

    span {
        font-weight: 600;
    }

    .md-button {
        height: 30px;
        width: 30px;
        min-width: 30px;
    }
}
</style>
